import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Receipt,
  Plus,
  FileText,
  CheckCircle2,
  XCircle,
  Clock3,
  UploadCloud,
  Eye,
  Download,
  Search,
  Pencil,
  Trash2,
  CircleDollarSign,
  BadgeCheck,
  FileCheck2,
  PanelLeftClose,
} from 'lucide-react';
import { formatCurrency } from '../dashboards/shared';
import { buildApiUrl } from '../../utils/apiSetup';
import { formatDate } from '../../utils/dateUtils';

const apiHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const statusClasses = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-700',
  'Pending Review': 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-rose-100 text-rose-700',
};

const emptyForm = {
  category_id: '',
  expense_type: '',
  expense_date: '',
  claimed_amount: '',
  payment_mode: 'UPI',
  currency: 'INR',
  description: '',
  remarks: '',
  declaration: false,
};

const EmployeeReimbursementPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState({
    total_claims: 0,
    pending_claims: 0,
    approved_claims: 0,
    rejected_claims: 0,
    total_claimed_amount: 0,
    total_approved_amount: 0,
  });
  const [activeView, setActiveView] = useState('dashboard');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingClaimId, setEditingClaimId] = useState(null);
  const [activeClaimForDocs, setActiveClaimForDocs] = useState(null);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [previewDocument, setPreviewDocument] = useState(null);

  const employeeProfile = useMemo(() => ({
    employee_code: user?.employee_code || '',
    employee_name: user?.employee_name || '',
    department: user?.department || '',
    designation: user?.designation || '',
    branch: user?.office_location || '',
  }), [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [claimsRes, catsRes, summaryRes] = await Promise.all([
        fetch(buildApiUrl('/api/v1/reimbursements/claims'), { headers: apiHeaders() }),
        fetch(buildApiUrl('/api/v1/reimbursements/categories'), { headers: apiHeaders() }),
        fetch(buildApiUrl('/api/v1/reimbursements/dashboard'), { headers: apiHeaders() }),
      ]);

      if (claimsRes.ok) {
        const claimsData = await claimsRes.json();
        setClaims(claimsData || []);
      }
      if (catsRes.ok) {
        const categoriesData = await catsRes.json();
        setCategories(categoriesData || []);
      }
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData || {});
      }
    } catch (error) {
      setMessage('Unable to load reimbursement data right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredClaims = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = claims;
    if (activeView === 'history') {
      result = claims.filter((claim) => ['Approved', 'Rejected'].includes(claim.status));
    }
    if (!q) return result;
    return result.filter((claim) => [claim.claim_number, claim.category_name, claim.status, claim.expense_type].join(' ').toLowerCase().includes(q));
  }, [claims, search, activeView]);

  const resetForm = () => {
    setShowForm(false);
    setEditingClaimId(null);
    setForm(emptyForm);
    setSelectedFiles([]);
  };

  const handleOpenCreate = () => {
    setActiveView('create');
    setShowForm(true);
    setEditingClaimId(null);
    setForm({ ...emptyForm, payment_mode: 'UPI', currency: 'INR' });
    setSelectedFiles([]);
    setActiveClaimForDocs(null);
  };

  const handleOpenEdit = (claim) => {
    setActiveView('create');
    setShowForm(true);
    setEditingClaimId(claim.id);
    setForm({
      category_id: claim.category_id || '',
      expense_type: claim.expense_type || '',
      expense_date: claim.expense_date ? claim.expense_date.split('T')[0] : '',
      claimed_amount: claim.claimed_amount || '',
      payment_mode: claim.payment_mode || 'UPI',
      currency: claim.currency || 'INR',
      description: claim.description || '',
      remarks: claim.remarks || '',
      declaration: Boolean(claim.declaration),
    });
    setSelectedFiles([]);
    setActiveClaimForDocs(null);
  };

  const handleCreateOrUpdateClaim = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      if (editingClaimId) {
        const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${editingClaimId}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...apiHeaders() },
          body: JSON.stringify({
            ...form,
            claimed_amount: Number(form.claimed_amount),
            declaration: Boolean(form.declaration),
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Unable to save claim');
        setMessage('Claim updated successfully.');
        setShowForm(false);
        setEditingClaimId(null);
        setActiveClaimForDocs(data);
        setActiveView('claims');
        await loadData();
        return;
      }

      const formData = new FormData();
      formData.append('category_id', form.category_id);
      formData.append('expense_type', form.expense_type);
      formData.append('expense_date', form.expense_date);
      formData.append('claimed_amount', String(Number(form.claimed_amount)));
      formData.append('payment_mode', form.payment_mode);
      formData.append('currency', form.currency);
      formData.append('description', form.description || '');
      formData.append('remarks', form.remarks || '');
      formData.append('declaration', String(Boolean(form.declaration)));
      selectedFiles.forEach((file) => formData.append('documents', file));

      const response = await fetch(buildApiUrl('/api/v1/reimbursements/claims'), {
        method: 'POST',
        headers: apiHeaders(),
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to save claim');
      setMessage('Claim draft created successfully.');
      setShowForm(false);
      setEditingClaimId(null);
      setSelectedFiles([]);
      setActiveClaimForDocs(data);
      setActiveView('claims');
      await loadData();
    } catch (error) {
      setMessage(error.message || 'Unable to save claim');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentSelection = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleUploadDocuments = async () => {
    if (!activeClaimForDocs?.id || selectedFiles.length === 0) {
      setMessage('Select at least one document to upload.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append('files', file));
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${activeClaimForDocs.id}/documents`), {
        method: 'POST',
        headers: apiHeaders(),
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Unable to upload document');
      }
      setMessage('Documents uploaded successfully.');
      setSelectedFiles([]);
      await loadData();
      const refreshed = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${activeClaimForDocs.id}`), { headers: apiHeaders() });
      if (refreshed.ok) {
        const claimData = await refreshed.json();
        setActiveClaimForDocs(claimData);
        setSelectedClaim(claimData);
      }
    } catch (error) {
      setMessage(error.message || 'Unable to upload documents');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!activeClaimForDocs?.id) return;
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${activeClaimForDocs.id}/documents/${documentId}`), {
        method: 'DELETE',
        headers: apiHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to remove document');
      setMessage('Document removed successfully.');
      const refreshed = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${activeClaimForDocs.id}`), { headers: apiHeaders() });
      if (refreshed.ok) {
        const claimData = await refreshed.json();
        setActiveClaimForDocs(claimData);
        setSelectedClaim(claimData);
      }
    } catch (error) {
      setMessage(error.message || 'Unable to remove document');
    }
  };

  const handleSubmitClaim = async (claimId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${claimId}/submit`), { method: 'POST', headers: apiHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to submit claim');
      setMessage('Claim submitted for review.');
      await loadData();
      setSelectedClaim(data);
      setActiveClaimForDocs(data);
    } catch (error) {
      setMessage(error.message || 'Unable to submit claim');
    }
  };

  const handleDeleteDraft = async (claimId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${claimId}`), { method: 'DELETE', headers: apiHeaders() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to delete draft');
      setMessage('Draft claim deleted.');
      await loadData();
      if (selectedClaim?.id === claimId) {
        setSelectedClaim(null);
      }
    } catch (error) {
      setMessage(error.message || 'Unable to delete draft');
    }
  };

  const handleViewClaim = async (claim) => {
    setActiveView('claims');
    try {
      const response = await fetch(buildApiUrl(`/api/v1/reimbursements/claims/${claim.id}`), { headers: apiHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to load claim');
      setSelectedClaim(data);
      setActiveClaimForDocs(data);
    } catch (error) {
      setMessage(error.message || 'Unable to load claim');
    }
  };

  const previewUrl = (documentItem) => `/api/v1/reimbursements/documents/${documentItem.id}/download`;

  if (loading) {
    return <div className="rounded-2xl bg-white p-8 shadow-sm">Loading reimbursement workspace…</div>;
  }

  return (
    <div className="space-y-6">
      {message ? <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">{message}</div> : null}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Employee Reimbursement</h2>
          <p className="text-sm text-slate-500">Create, submit, and track your reimbursement claims.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setActiveView('dashboard')} className={`rounded-lg px-3 py-2 text-sm font-medium ${activeView === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>Dashboard</button>
          <button onClick={handleOpenCreate} className={`rounded-lg px-3 py-2 text-sm font-medium ${activeView === 'create' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}><Plus className="mr-1 inline h-4 w-4" />Create New Claim</button>
          <button onClick={() => setActiveView('claims')} className={`rounded-lg px-3 py-2 text-sm font-medium ${activeView === 'claims' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>My Claims</button>
          <button onClick={() => setActiveView('history')} className={`rounded-lg px-3 py-2 text-sm font-medium ${activeView === 'history' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700'}`}>Claim History</button>
        </div>
      </div>

      {activeView === 'dashboard' ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-blue-600 to-indigo-700 p-5 text-white shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100">Total Claims</p>
                <p className="text-3xl font-semibold">{summary.total_claims}</p>
              </div>
              <div className="rounded-full bg-white/20 p-3"><Receipt className="h-6 w-6" /></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Claims</p>
                <p className="text-3xl font-semibold text-slate-900">{summary.pending_claims}</p>
              </div>
              <div className="rounded-full bg-amber-100 p-3"><Clock3 className="h-6 w-6 text-amber-700" /></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved Claims</p>
                <p className="text-3xl font-semibold text-slate-900">{summary.approved_claims}</p>
              </div>
              <div className="rounded-full bg-emerald-100 p-3"><CheckCircle2 className="h-6 w-6 text-emerald-700" /></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rejected Claims</p>
                <p className="text-3xl font-semibold text-slate-900">{summary.rejected_claims}</p>
              </div>
              <div className="rounded-full bg-rose-100 p-3"><XCircle className="h-6 w-6 text-rose-700" /></div>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Approved Amount</p>
                <p className="text-2xl font-semibold text-slate-900">{formatCurrency(summary.total_approved_amount || 0)}</p>
              </div>
              <div className="rounded-full bg-violet-100 p-3"><CircleDollarSign className="h-6 w-6 text-violet-700" /></div>
            </div>
          </div>
        </div>
      ) : null}

      {activeView === 'create' ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{editingClaimId ? 'Edit Claim' : 'Create New Claim'}</h3>
              <p className="text-sm text-slate-500">Complete the claim details and attach supporting documents.</p>
            </div>
            <button onClick={resetForm} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Cancel</button>
          </div>

          <div className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Employee Code</label>
              <input readOnly value={employeeProfile.employee_code} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Employee Name</label>
              <input readOnly value={employeeProfile.employee_name} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Department</label>
              <input readOnly value={employeeProfile.department} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Designation</label>
              <input readOnly value={employeeProfile.designation} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Branch</label>
              <input readOnly value={employeeProfile.branch} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" />
            </div>
          </div>

          <form onSubmit={handleCreateOrUpdateClaim} className="grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Category</label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required>
                <option value="">Select category</option>
                {categories.map((category) => <option key={category.id} value={category.id}>{category.category_name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Expense Type</label>
              <input value={form.expense_type} onChange={(e) => setForm({ ...form, expense_type: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Expense Date</label>
              <input type="date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Claim Amount</label>
              <input type="number" step="0.01" value={form.claimed_amount} onChange={(e) => setForm({ ...form, claimed_amount: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Payment Mode</label>
              <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required>
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Debit Card">Debit Card</option>
                <option value="Credit Card">Credit Card</option>
                <option value="Net Banking">Net Banking</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Currency</label>
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" required>
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="AED">AED</option>
              </select>
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" rows="3" />
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-700">Remarks</label>
              <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2" rows="2" />
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <UploadCloud className="h-5 w-5 text-blue-600" />
                <div>
                  <h4 className="font-semibold text-slate-900">Upload Documents</h4>
                  <p className="text-sm text-slate-500">PDF, PNG, JPG, and JPEG files are supported. Multiple files can be selected.</p>
                </div>
              </div>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" multiple onChange={handleDocumentSelection} className="block w-full text-sm text-slate-600" />
              {selectedFiles.length > 0 ? (
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {selectedFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span>{file.name}</span>
                      <button type="button" onClick={() => handleRemoveSelectedFile(index)} className="text-xs font-semibold text-rose-600">Remove</button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {activeClaimForDocs ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800">Attached documents</p>
                    <span className="text-xs text-slate-500">Claim #{activeClaimForDocs.claim_number}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(activeClaimForDocs.documents || []).map((document) => (
                      <div key={document.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        <FileText className="h-4 w-4" />
                        <span>{document.file_name}</span>
                        <button type="button" onClick={() => setPreviewDocument(document)} className="text-blue-600">Preview</button>
                        <button type="button" onClick={() => handleDeleteDocument(document.id)} className="text-rose-600">Remove</button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="lg:col-span-2 flex items-center gap-3">
              <input type="checkbox" checked={form.declaration} onChange={(e) => setForm({ ...form, declaration: e.target.checked })} />
              <span className="text-sm text-slate-700">I certify that this reimbursement claim is genuine and all uploaded documents are authentic.</span>
            </div>
            <div className="lg:col-span-2 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
              <button type="submit" disabled={submitting} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">{submitting ? 'Saving…' : editingClaimId ? 'Update Claim' : 'Save Draft'}</button>
              {activeClaimForDocs ? (
                <button type="button" onClick={handleUploadDocuments} disabled={uploading || selectedFiles.length === 0} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">{uploading ? 'Uploading…' : 'Upload Documents'}</button>
              ) : null}
            </div>
          </form>
        </div>
      ) : null}

      {(activeView === 'claims' || activeView === 'history' || activeView === 'dashboard') ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{activeView === 'history' ? 'Claim History' : 'My Claims'}</h3>
              <p className="text-sm text-slate-500">Track your claims and access supporting documents.</p>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <Search className="h-4 w-4" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search claims" className="w-full bg-transparent outline-none" />
            </label>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-3">Claim #</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted Date</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredClaims.map((claim) => (
                  <tr key={claim.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{claim.claim_number}</td>
                    <td className="px-4 py-3 text-slate-600">{claim.category_name || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{formatCurrency(Number(claim.claimed_amount || 0), claim.currency || 'INR')}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[claim.status] || 'bg-slate-100 text-slate-700'}`}>{claim.status}</span></td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(claim.submitted_at, '—')}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => handleViewClaim(claim)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-slate-700"><Eye className="h-4 w-4" />View</button>
                        {claim.status === 'Draft' ? (
                          <>
                            <button onClick={() => handleOpenEdit(claim)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-slate-700"><Pencil className="h-4 w-4" />Edit</button>
                            <button onClick={() => handleDeleteDraft(claim.id)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-3 py-1.5 text-rose-700"><Trash2 className="h-4 w-4" />Delete</button>
                          </>
                        ) : null}
                        {claim.status === 'Draft' ? <button onClick={() => handleSubmitClaim(claim.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-white">Submit</button> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {selectedClaim ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Claim Details</h3>
              <p className="text-sm text-slate-500">{selectedClaim.claim_number}</p>
            </div>
            <button onClick={() => setSelectedClaim(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"><PanelLeftClose className="mr-1 inline h-4 w-4" />Hide</button>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Employee Information</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Employee Code</p><p className="text-sm font-medium text-slate-900">{selectedClaim.employee_code || employeeProfile.employee_code}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Employee Name</p><p className="text-sm font-medium text-slate-900">{selectedClaim.employee_name || employeeProfile.employee_name}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Department</p><p className="text-sm font-medium text-slate-900">{selectedClaim.department || employeeProfile.department}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Designation</p><p className="text-sm font-medium text-slate-900">{selectedClaim.designation || employeeProfile.designation}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Branch</p><p className="text-sm font-medium text-slate-900">{selectedClaim.branch || employeeProfile.branch}</p></div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Expense Details</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Category</p><p className="text-sm font-medium text-slate-900">{selectedClaim.category_name || '—'}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Expense Type</p><p className="text-sm font-medium text-slate-900">{selectedClaim.expense_type}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Expense Date</p><p className="text-sm font-medium text-slate-900">{formatDate(selectedClaim.expense_date, '—')}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Claim Amount</p><p className="text-sm font-medium text-slate-900">{formatCurrency(Number(selectedClaim.claimed_amount || 0), selectedClaim.currency || 'INR')}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Payment Mode</p><p className="text-sm font-medium text-slate-900">{selectedClaim.payment_mode}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-slate-500">Status</p><p className="text-sm font-medium text-slate-900">{selectedClaim.status}</p></div>
                  <div className="md:col-span-2"><p className="text-xs uppercase tracking-wide text-slate-500">Description</p><p className="text-sm font-medium text-slate-900">{selectedClaim.description || '—'}</p></div>
                  <div className="md:col-span-2"><p className="text-xs uppercase tracking-wide text-slate-500">Remarks</p><p className="text-sm font-medium text-slate-900">{selectedClaim.remarks || '—'}</p></div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Uploaded Documents</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedClaim.documents || []).map((document) => (
                    <div key={document.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                      <FileText className="h-4 w-4" />
                      <span>{document.file_name}</span>
                      <button type="button" onClick={() => setPreviewDocument(document)} className="text-blue-600">Preview</button>
                      <a href={previewUrl(document)} target="_blank" rel="noreferrer" className="text-emerald-600"><Download className="h-4 w-4" /></a>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Timeline</h4>
                <ul className="space-y-3 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><BadgeCheck className="mt-0.5 h-4 w-4 text-emerald-600" /><span>Claim created on {selectedClaim.created_at ? new Date(selectedClaim.created_at).toLocaleString('en-IN') : '—'}</span></li>
                  {selectedClaim.submitted_at ? <li className="flex items-start gap-2"><FileCheck2 className="mt-0.5 h-4 w-4 text-blue-600" /><span>Claim submitted on {new Date(selectedClaim.submitted_at).toLocaleString('en-IN')}</span></li> : null}
                  {selectedClaim.approved_at ? <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /><span>Claim approved on {new Date(selectedClaim.approved_at).toLocaleString('en-IN')}</span></li> : null}
                  {selectedClaim.rejected_at ? <li className="flex items-start gap-2"><XCircle className="mt-0.5 h-4 w-4 text-rose-600" /><span>Claim rejected on {new Date(selectedClaim.rejected_at).toLocaleString('en-IN')}</span></li> : null}
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h4 className="mb-3 font-semibold text-slate-900">Approval Status</h4>
                <p className="text-sm text-slate-700">{selectedClaim.status}</p>
                {selectedClaim.rejection_reason ? <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">Rejection reason: {selectedClaim.rejection_reason}</p> : null}
                {selectedClaim.admin_remarks ? <p className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">Admin remarks: {selectedClaim.admin_remarks}</p> : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {previewDocument ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">{previewDocument.file_name}</h3>
              <button onClick={() => setPreviewDocument(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">Close</button>
            </div>
            <div className="h-[70vh] overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              {previewDocument.file_type?.toLowerCase() === 'pdf' ? (
                <iframe title={previewDocument.file_name} src={previewUrl(previewDocument)} className="h-full w-full" />
              ) : (
                <img src={previewUrl(previewDocument)} alt={previewDocument.file_name} className="h-full w-full object-contain" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EmployeeReimbursementPage;
